import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3 } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Toolbar } from '@/components/Toolbar'
import { FormField } from '@/components/FormField'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { EmptyState } from '@/components/EmptyState'
import { DataTable } from '@/components/DataTable'
import { useApAging } from '@/features/ap/queries'
import type { ApAgingBill, ApAgingVendorRow } from '@/features/ap/types'
import { formatInvoiceMoney } from '@/features/invoicing/format'
import { getUserLocale } from '@/lib/locale'

function fmtMoney(v: number, currency: string) {
  return formatInvoiceMoney(v, currency)
}

/**
 * Phase 4 - AP Aging report. Mirrors the AR Aging page so the two
 * reports can sit side-by-side mentally; same buckets, same layout.
 * Numbers reconcile to the AP control account on the Balance Sheet.
 */
export function ApAgingPage() {
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const [asOf, setAsOf] = useState<string>(todayISO)
  const { data, isLoading } = useApAging(asOf)
  const report = data?.report
  // Empty-bills fallback: use the tenant's regional currency instead of
  // hardcoded USD so a UK tenant with no bills sees £ headers, not $.
  const currency = report?.bills[0]?.currency || getUserLocale().currency

  const vendorColumns = useMemo(() => [
    { accessorKey: 'vendor_display_name', header: 'Vendor',
      cell: ({ row }: { row: { original: ApAgingVendorRow } }) => row.original.vendor_display_name,
    },
    { accessorKey: 'current', header: 'Current',
      cell: ({ row }: { row: { original: ApAgingVendorRow } }) => fmtMoney(row.original.current, currency),
    },
    { accessorKey: 'd1_30', header: '1–30',
      cell: ({ row }: { row: { original: ApAgingVendorRow } }) => fmtMoney(row.original.d1_30, currency),
    },
    { accessorKey: 'd31_60', header: '31–60',
      cell: ({ row }: { row: { original: ApAgingVendorRow } }) => fmtMoney(row.original.d31_60, currency),
    },
    { accessorKey: 'd61_90', header: '61–90',
      cell: ({ row }: { row: { original: ApAgingVendorRow } }) => fmtMoney(row.original.d61_90, currency),
    },
    { accessorKey: 'd91_plus', header: '91+',
      cell: ({ row }: { row: { original: ApAgingVendorRow } }) => fmtMoney(row.original.d91_plus, currency),
    },
    { accessorKey: 'total', header: 'Total',
      cell: ({ row }: { row: { original: ApAgingVendorRow } }) =>
        <span className="font-semibold">{fmtMoney(row.original.total, currency)}</span>,
    },
  ], [currency])

  const billColumns = useMemo(() => [
    { accessorKey: 'bill_number', header: 'Bill',
      cell: ({ row }: { row: { original: ApAgingBill } }) => (
        <Link to={`/invoicing/bills/${row.original.id}`} className="font-medium text-primary hover:underline">
          {row.original.bill_number}
        </Link>
      ),
    },
    { accessorKey: 'vendor_display_name', header: 'Vendor' },
    { accessorKey: 'due_date', header: 'Due' },
    { accessorKey: 'days_past_due', header: 'Days past',
      cell: ({ row }: { row: { original: ApAgingBill } }) =>
        row.original.days_past_due > 0 ? row.original.days_past_due : <span className="text-muted-foreground">-</span>,
    },
    { accessorKey: 'balance', header: 'Balance',
      cell: ({ row }: { row: { original: ApAgingBill } }) => fmtMoney(row.original.balance, row.original.currency),
    },
  ], [])

  return (
    <Page
      title="AP Aging"
      description="Outstanding bill balances bucketed by how far past their due date they are. Numbers reconcile to the AP control account on the Balance Sheet."
    >
      <Toolbar align="start" className="mb-3">
        <FormField label="As of" type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="w-48" />
      </Toolbar>
      {isLoading ? (
        <Card><div className="flex justify-center py-16"><LoadingSpinner /></div></Card>
      ) : !report ? (
        <Card><EmptyState icon={BarChart3} title="No AP aging data" description="Once bills are outstanding, this report will populate." /></Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-6">
              {report.buckets.map(b => (
                <div key={b.key} className="rounded-md border p-3">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{b.label}</div>
                  <div className="mt-1 text-lg font-semibold">{fmtMoney(report.totals[b.key], currency)}</div>
                </div>
              ))}
              <div className="rounded-md border bg-muted/40 p-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Total</div>
                <div className="mt-1 text-lg font-bold">{fmtMoney(report.totals.total, currency)}</div>
              </div>
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="mb-3 text-sm font-semibold">By vendor</div>
              <DataTable
                columns={vendorColumns as never}
                rows={report.vendors}
                empty={<EmptyState icon={BarChart3} title="No outstanding vendors" description="All bills are paid." />}
              />
            </div>
          </Card>
          <Card>
            <div className="p-4">
              <div className="mb-3 text-sm font-semibold">By bill</div>
              <DataTable
                columns={billColumns as never}
                rows={report.bills}
                empty={<EmptyState icon={BarChart3} title="No open bills" description="Nothing outstanding as of this date." />}
              />
            </div>
          </Card>
        </div>
      )}
    </Page>
  )
}
